<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import { AssetType } from '@xcel-next/core/enums'

import { getAxios } from '../../../api/axios'
import { useClient } from '../../../utils/client-only'
import { useUrlHelper } from '../../../composables/use-url-helper'
import Dialog from '../../shared/Dialog.vue'
import ProgressBar from '../../shared/ProgressBar.vue'
import Rating from '../../shared/Rating.vue'
import RatingCta from './RatingCta.vue'
import { useKeepTabbingFocus, isUserTabbing } from '../../../composables/use-keep-tabbing-focus'
import { useAssetStore } from '../../../stores/asset'
import { useNotifyStore } from '../../../stores/notify'
import { useSessionStore } from '../../../stores/session'
import { useConfigStore } from '../../../stores/config'
import { usePageStore } from '../../../stores/page'

import type { ComposedPageMeta } from '@xcel-next/composer-schema'
import type { AssetSchemaType } from '@xcel-next/types'

type CommentDocumentData = NonNullable<AssetSchemaType<AssetType.Comment>['documentData']>
type AggregatedRatings = Record<1 | 2 | 3 | 4 | 5, number>
type PartialAggregatedRatings = NonNullable<ComposedPageMeta['rating']>['aggregated']
type RaterListEntry = NonNullable<NonNullable<ComposedPageMeta['rating']>['raterList']>[number]

const props = defineProps<{
  pageMeta?: ComposedPageMeta
}>()

const emit = defineEmits<{
  'close:dialog': []
}>()

const { isClient } = useClient()
const { getQueryParams } = useUrlHelper()
const { keepTabbingFocus, removeTabbingEventListeners } = useKeepTabbingFocus()

const sessionStore = useSessionStore()
const { isLoggedIn, user, profile } = storeToRefs(sessionStore)

const notifyStore = useNotifyStore()
const { notifications } = storeToRefs(notifyStore)
const { showNotification } = notifyStore

const { rsConfig } = storeToRefs(useConfigStore())
const { brandFromStore } = storeToRefs(usePageStore())

const { getAssetByQuery, patchAsset, postAsset, postPublicAsset } = useAssetStore()

const assetType = AssetType.Comment // Flag to toggle between public and user-based ratings
const publicRatingEnabled = ref(false)
const publicRateStorage = ref<Record<string, { ratingId: string }>>({})
const userRatingAsset = ref<Partial<CommentDocumentData>>({})
const latestValidRate = ref(0)
const existingRatingId = ref<string | null>(null)
const changeExistingRating = ref(false)

const normalizeAggregatedRatings = (aggregated?: PartialAggregatedRatings): AggregatedRatings => ({
  1: aggregated?.[1] ?? 0,
  2: aggregated?.[2] ?? 0,
  3: aggregated?.[3] ?? 0,
  4: aggregated?.[4] ?? 0,
  5: aggregated?.[5] ?? 0
})

const aggregatedRatings = ref<AggregatedRatings>(normalizeAggregatedRatings(props.pageMeta?.rating?.aggregated))
const raterList = ref<RaterListEntry[]>(props.pageMeta?.rating?.raterList ?? [])
const endlessLoading = ref(true)
const errorMsg = ref<{ rating: string | null, comment: string | null } | null>(null)
const showRatingOverlay = ref(false)
const showRatingList = ref(false)
const preventSubmit = ref(false)
const isProcessing = ref<boolean | null>(null)
const cookbookUrl = ref('')

const dialog = ref<InstanceType<typeof Dialog> | null>(null)
const ratingListItems = ref<HTMLElement | null>(null)

const ratingQuantity = computed(() => Object.values(aggregatedRatings.value).reduce((sum, val) => sum + val, 0))

const ratingAverage = computed(() => {
  if (!ratingQuantity.value) return 0
  const sum = Object.entries(aggregatedRatings.value)
    .reduce((total, [key, count]) => total + Number(key) * count, 0)
  return sum / ratingQuantity.value
})

const getEmptyRatingAsset = (): Partial<CommentDocumentData> => ({
  rating: 5,
  comment: '',
  asset: {
    assetId: props.pageMeta?.id ?? '',
    assetType: props.pageMeta?.pageType as AssetType,
    brand: brandFromStore.value
  }
})

const getEmptyErrorMsg = () => ({
  comment: null,
  rating: null
})

const handleChangedAssetValue = (newValue: unknown, oldValue: unknown): void => {
  if (newValue !== oldValue && !isProcessing.value && showRatingOverlay.value) {
    preventSubmit.value = false
  }
}

watch(() => userRatingAsset.value.rating, (newValue, oldValue) => handleChangedAssetValue(newValue, oldValue))
watch(() => userRatingAsset.value.comment, (newValue, oldValue) => handleChangedAssetValue(newValue, oldValue))

const keepTabbingFocusInRatingOverlay = (): void => {
  nextTick(() => {
    const focusableElements = ['a, button:not(:disabled), input, textarea, select, [tabindex]:not([tabindex="-1"])']
    removeTabbingEventListeners()
    keepTabbingFocus(dialog.value!.$el, focusableElements, 0, () => {
      removeTabbingEventListeners()
      emit('close:dialog')
    })
    // Set focus deterministically instead of relying on the trap's delayed auto-focus
    if (isUserTabbing()) {
      dialog.value!.$el.querySelector(focusableElements[0])?.focus()
    }
  })
}

watch(changeExistingRating, () => {
  if (changeExistingRating.value) {
    keepTabbingFocusInRatingOverlay()
  }
})

watch(showRatingOverlay, () => {
  if (showRatingOverlay.value) {
    keepTabbingFocusInRatingOverlay()
  }
})

watch(showRatingList, () => {
  if (showRatingList.value) {
    keepTabbingFocusInRatingOverlay()
  }
})

watch(notifications, () => {
  if (showRatingOverlay.value) {
    keepTabbingFocusInRatingOverlay()
  }
}, { deep: true }) // in vue3 arrays have to be watched deeply

const getEnrichedComments = async (): Promise<void> => {
  isProcessing.value = true
  const axios = await getAxios()
  const query = new URLSearchParams({
    brand: brandFromStore.value ?? '',
    assetType: AssetType.Article,
    assetId: props.pageMeta?.id ?? '',
    offset: String(raterList.value.length)
  })
  const result = await axios.get(`${rsConfig.value.orchestrationServiceUrl}/assets/comments/enriched?${query}`)
  if (result) {
    raterList.value = [...raterList.value, ...result.data]
    endlessLoading.value = result.data.length === 10
    keepTabbingFocusInRatingOverlay()
  }
  nextTick(() => {
    if (ratingListItems.value) {
      ratingListItems.value.scrollTop = ratingListItems.value.scrollHeight
    }
  })
  isProcessing.value = null // vue3: data attribute will be removed (NOT if set to false)
}

const loadExistingUserRating = async (): Promise<void> => {
  const result = await getAssetByQuery({
    type: assetType,
    q: `documentData.asset.assetId.raw:${props.pageMeta?.id} AND protectedMeta.creator.raw:${user.value?.userId}`,
    sort: 'protectedMeta.dateCreated:asc'
  })
  if (result) {
    preventSubmit.value = true
    existingRatingId.value = result.id ?? null
    userRatingAsset.value = result.documentData ?? {}
    latestValidRate.value = result.documentData?.rating ?? 0
  }
}

const handleRatingRedirect = (): void => {
  window.location.href = cookbookUrl.value
}

const openRatingOverlay = async (): Promise<void> => {
  isProcessing.value = true
  changeExistingRating.value = false
  userRatingAsset.value = getEmptyRatingAsset()
  errorMsg.value = getEmptyErrorMsg()
  if (isLoggedIn.value) {
    await loadExistingUserRating()
    if (document?.referrer?.includes('/anmelden')) {
      window.history.pushState({}, '', window.location.href.split('?')[0])
    }
  } else if (publicRatingEnabled.value) {
    const publicRating = publicRateStorage.value[userRatingAsset.value.asset!.assetId]
    if (publicRating) {
      preventSubmit.value = true
      existingRatingId.value = publicRating.ratingId
    }
  }
  showRatingOverlay.value = true
  isProcessing.value = null // vue3: data attribute will be removed (NOT if set to false)
}

const isFormValid = (): boolean => {
  userRatingAsset.value.comment = userRatingAsset.value.comment?.trim() ?? ''

  if (userRatingAsset.value.rating === 0) {
    errorMsg.value!.rating = 'Bitte vergib eine Bewertung.'
  } else {
    errorMsg.value!.rating = null

    if ((userRatingAsset.value.rating ?? 0) < 4 && userRatingAsset.value.comment.length < 10) {
      errorMsg.value!.comment = `Bitte hinterlasse einen Kommentar${userRatingAsset.value.comment.length > 0 ? ' mit mehr Aussagekraft' : ''}, warum dir dieses Rezept weniger gut gefallen hat.`
    } else {
      errorMsg.value!.comment = null
    }
  }

  return !Object.values(errorMsg.value!).some(message => message)
}

const closeRatingOverlay = (): void => {
  showRatingOverlay.value = showRatingList.value = false
}

const submit = async (): Promise<void> => {
  if (!publicRatingEnabled.value && !isLoggedIn.value) {
    window.location.href = `/anmelden?rts=${encodeURIComponent(JSON.stringify({ action: 'rate' }))}`
    return
  }
  isProcessing.value = true
  try {
    if (!isFormValid()) {
      isProcessing.value = null
      return
    }
    if (existingRatingId.value && isLoggedIn.value) {
      await patchAsset({
        type: assetType,
        id: existingRatingId.value,
        asset: { documentData: userRatingAsset.value } as AssetSchemaType<AssetType.Comment>
      })
      aggregatedRatings.value[latestValidRate.value as keyof AggregatedRatings]--
      aggregatedRatings.value[userRatingAsset.value.rating as keyof AggregatedRatings]++
    } else {
      const result = !isLoggedIn.value && !existingRatingId.value && publicRatingEnabled.value
        ? await postPublicAsset({
          type: assetType,
          asset: { documentData: userRatingAsset.value } as AssetSchemaType<AssetType.Comment>
        }, brandFromStore.value!)
        : await postAsset({
          type: assetType,
          asset: { documentData: userRatingAsset.value } as AssetSchemaType<AssetType.Comment>
        })
      if (result) {
        if (publicRatingEnabled.value) {
          publicRateStorage.value[userRatingAsset.value.asset!.assetId] = {
            ratingId: result.id ?? ''
          }
          window.localStorage.setItem('publicRateStorage', JSON.stringify(publicRateStorage.value))
          closeRatingOverlay()
        }
        existingRatingId.value = result.id ?? null
        raterList.value = [{
          name: profile.value?.documentData?.displayName,
          rating: userRatingAsset.value.rating,
          date: new Date().toISOString()
        }, ...raterList.value]
        aggregatedRatings.value[userRatingAsset.value.rating as keyof AggregatedRatings]++
      }
    }
    latestValidRate.value = userRatingAsset.value.rating ?? 0
    showNotification({
      title: 'Vielen Dank',
      message: 'Deine Bewertung wurde erfolgreich gespeichert.'
    })
    preventSubmit.value = true
    changeExistingRating.value = false
  } catch (e) {
    console.log(e)
    showNotification({
      type: 'error',
      title: 'Fehler beim Speichern',
      message: 'Es ist ein technischer Fehler aufgetreten. Bitte versuche es zu einem späteren Zeitpunkt erneut.'
    })
  }
  isProcessing.value = null
}

const abortChange = (): void => {
  changeExistingRating.value = false
  errorMsg.value = getEmptyErrorMsg()
}

onMounted(() => {
  if (sessionStorage.getItem('bx-restore-tabbing')) {
    document.body.classList.add('bx-js-user-is-tabbing')
    sessionStorage.removeItem('bx-restore-tabbing')
  }

  if (getQueryParams()?.action === 'rate') {
    openRatingOverlay()
  }

  if (getQueryParams()?.back) {
    cookbookUrl.value = window.location.origin + decodeURIComponent(getQueryParams().back)
  }

  if (publicRatingEnabled.value) {
    publicRateStorage.value = JSON.parse(window.localStorage.getItem('publicRateStorage') || '{}')
  }
  endlessLoading.value = raterList.value.length === 10
})
</script>

<template>
  <div>
    <div class="bx-recipe__rating"
         tabindex="0"
         @click="openRatingOverlay()"
         @keyup.enter="openRatingOverlay()">
      <RatingCta :rating-quantity="`(${ratingQuantity})`"
                 :rating-average="ratingAverage" />
    </div>

    <Dialog v-if="isClient && showRatingOverlay"
            ref="dialog"
            title="Rezeptbewertung"
            class="bx-dialog--recipe-rating bx-recipe__rating-dialog"
            @close="closeRatingOverlay()">
      <template #header>
        <div class="bx-dialog__title">
          <h2>{{ showRatingList ? 'Bewertungsdetails' : 'Rezeptbewertung' }}</h2>
        </div>
      </template>

      <div v-if="existingRatingId && !showRatingList"
           class="bx-content__continuous-text bx-border-bottom bx-typo--16px">
        <p>
          Du hast dieses Rezept bereits bewertet.<br>
          <template v-if="isLoggedIn">
            Möchtest du deine
            <button class="bx-button--link bx-typo--link"
                    @click="changeExistingRating ? abortChange() : changeExistingRating = true">
              Bewertung ändern
            </button>?
            <div v-if="cookbookUrl"
                 class="bx-button--link bx-typo--link bx-margin-top-12"
                 tabindex="0"
                 @click="handleRatingRedirect()">
              Zurück zum Kochbuch
            </div>
          </template>
        </p>
      </div>

      <form v-if="(!existingRatingId || changeExistingRating) && !showRatingList"
            class="bx-form bx-form--pur bx-typo--form bx-recipe__rating-form"
            @submit.prevent="submit()">
        <template v-if="isLoggedIn || publicRatingEnabled">
          <div :class="{'bx-has-error': errorMsg?.rating}">
            <label class="bx-form__label">
              Wie viele Sterne gibst du dem Rezept?
            </label>
            <Rating :show-average="false"
                    :editable="true"
                    :average="userRatingAsset.rating"
                    :class="{'bx-has-error': errorMsg?.rating}"
                    @update:average="userRatingAsset.rating = $event" />
            <span v-if="errorMsg?.rating"
                  class="bx-form__message bx-form__message--error">
              {{ errorMsg.rating }}
            </span>
          </div>
          <div :class="{'bx-has-error': errorMsg?.comment}">
            <label for="comment"
                   class="bx-form__label">
              Teile deine Erfahrungen mit der Lecker-Redaktion. (max. 500 Zeichen)
            </label>
            <textarea id="comment"
                      v-model="userRatingAsset.comment"
                      class="bx-form__textarea"
                      :class="{'bx-has-error': errorMsg?.comment}"
                      title="Kommentar"
                      placeholder="Dein Kommentar wird nicht öffentlich auf Lecker.de sichtbar sein."
                      rows="2"
                      maxlength="500" />
            <span v-if="errorMsg?.comment"
                  class="bx-form__message bx-form__message--error">
              {{ errorMsg.comment }}
            </span>
            <div class="bx-form__item bx-form__item--centered">
              <button v-if="existingRatingId"
                      class="bx-form__button bx-form__button--secondary"
                      @click.prevent="abortChange()">
                Abbrechen
              </button>
              <button type="submit"
                      class="bx-form__button"
                      :disabled="isProcessing || preventSubmit"
                      :data-loader="isProcessing">
                Bewertung speichern
              </button>
            </div>
            <div v-if="!existingRatingId && cookbookUrl"
                 class="bx-form__item bx-form__item--centered">
              <div class="bx-button--link bx-typo--link bx-margin-top-12"
                   tabindex="0"
                   @click="handleRatingRedirect()">
                Zurück zum Kochbuch
              </div>
            </div>
          </div>
        </template>

        <div v-else>
          <p class="bx-form__item--centered">
            Melde dich an, um eine Bewertung abzugeben
          </p>
          <div class="bx-form__item bx-form__item--centered">
            <button type="submit"
                    class="bx-form__button"
                    :disabled="isProcessing || preventSubmit"
                    :data-loader="isProcessing">
              Zur Anmeldung
            </button>
          </div>
        </div>
      </form>

      <div v-if="!showRatingList && !publicRatingEnabled"
           class="bx-recipe__rating-aggregation">
        <h3>{{ ratingQuantity }} abgegebene {{ `Bewertung${ratingQuantity !== 1 ? 'en' : ''}` }}</h3>
        <Rating size="medium"
                :show-average="true"
                :average="ratingAverage"
                average-postfix=" von 5"
                :average-fraction-digits="1" />
        <ProgressBar v-for="n in [5, 4, 3, 2, 1]"
                     :key="`ratingsProgress${n}`"
                     :partial-count="aggregatedRatings[n as keyof AggregatedRatings]"
                     :partial-name="`${n} ${n === 1 ? 'Stern' : 'Sterne'}`"
                     :total-count="ratingQuantity" />
        <button v-if="raterList.length > 0"
                class="bx-button--link bx-typo--link bx-recipe__rating-list-toggle"
                @click.prevent="showRatingList = !showRatingList">
          Alle Bewertungen anzeigen
        </button>
      </div>

      <div v-if="showRatingList && !publicRatingEnabled"
           class="bx-recipe__rating-list bx-form">
        <button class="bx-button--link bx-typo--link bx-recipe__rating-list-toggle"
                @click.stop="showRatingList = !showRatingList">
          Zurück zur Rezeptbewertung
        </button>
        <div class="bx-recipe__rating-list-items bx-recipe__rating-list-items--head">
          <div class="bx-typo--14px">
            <strong>Bewertung</strong>
            <strong>Benutzer</strong>
            <strong>Datum</strong>
          </div>
        </div>
        <div ref="ratingListItems"
             class="bx-recipe__rating-list-items">
          <div v-for="(rating, index) in raterList"
               :key="index"
               class="bx-typo--14px">
            <Rating size="small"
                    :average="rating.rating" />
            <span>{{ rating.name }}</span>
            <time :datetime="rating.date">{{ rating.date ? new Date(rating.date).toLocaleDateString('de-de', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '' }}</time>
          </div>
        </div>
        <div v-if="ratingQuantity"
             class="bx-form__item bx-form__item--centered bx-typo--form">
          <button class="bx-form__button"
                  :disabled="isProcessing || !endlessLoading"
                  :data-loader="isProcessing"
                  @click.prevent="getEnrichedComments()">
            mehr Bewertungen anzeigen
          </button>
        </div>
      </div>
    </Dialog>
  </div>
</template>
